import { useEffect, useState } from "preact/hooks";
import { t } from "../i18n";
import { Icon } from "../icon";
import * as S from "../state";

interface TestResult {
  verdict: string;
  mos: number;
  rtt_ms: number;
  loss_pct: number;
  jitter_ms: number;
  codec?: string;
}

export function Diagnostics() {
  const [echo, setEcho] = useState(S.site.value?.echo_test ?? "");
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<TestResult | null>(null);
  const [error, setError] = useState("");
  const [mic, setMic] = useState(false);
  const [silent, setSilent] = useState(false);
  const lvl = S.micLevel.value;

  useEffect(() => () => void S.cmd({ cmd: "diag.mic_stop" }), []);
  // no sound at all for 3 s of metering: say so
  useEffect(() => {
    if (!mic) return setSilent(false);
    let loud = false;
    const watch = setTimeout(() => setSilent(!loud), 3000);
    const stop = S.micLevel.subscribe((l) => {
      if ((l?.peak_db ?? -90) > -60) loud = true;
    });
    return () => {
      clearTimeout(watch);
      stop();
    };
  }, [mic]);

  const runTest = async () => {
    setTesting(true);
    setResult(null);
    setError("");
    const r = await S.cmd({ cmd: "diag.call_test", account: S.firstAccount.value, to: echo, seconds: 10 });
    setTesting(false);
    if (r.ok) setResult(r.data as unknown as TestResult);
    else setError(t("diag.call_failed"));
  };
  const toggleMic = async () => {
    if (mic) {
      await S.cmd({ cmd: "diag.mic_stop" });
      S.micLevel.value = null;
      setMic(false);
    } else {
      const r = await S.cmd({ cmd: "diag.mic_start" });
      setMic(!!r.ok);
    }
  };
  const pct = lvl?.rms_db !== undefined ? Math.max(0, Math.min(100, ((lvl.rms_db + 60) / 60) * 100)) : 0;
  const caps: [string, boolean][] = [
    ["diag.cap.webrtc", typeof RTCPeerConnection !== "undefined"],
    ["diag.cap.sink", typeof (HTMLMediaElement.prototype as { setSinkId?: unknown }).setSinkId === "function"],
    ["diag.cap.notifications", typeof Notification !== "undefined" && Notification.permission === "granted"],
  ];

  return (
    <div class="page">
      <button type="button" class="btn link" style={{ paddingLeft: 0 }} onClick={() => (S.page.value = "settings")}>
        ‹ {t("settings.title")}
      </button>
      <h1>{t("diag.title")}</h1>

      <h2>{t("diag.quality")}</h2>
      <div class="card">
        <p class="muted" style={{ marginTop: 0 }}>{t("diag.quality_help")}</p>
        <div class="row">
          <label class="field" style={{ flex: 1, margin: 0 }}>
            <span>{t("diag.echo_number")}</span>
            <input type="text" value={echo} onInput={(e) => setEcho((e.target as HTMLInputElement).value)} />
          </label>
          <button type="button" class="btn primary" style={{ alignSelf: "flex-end" }} disabled={testing || !echo || S.registration.value !== "registered"} onClick={() => void runTest()}>
            {testing ? t("diag.measuring") : t("diag.test_quality")}
          </button>
        </div>
        {error && <p class="danger-text">{error}</p>}
        {result && (
          <dl class="stats" style={{ marginTop: "16px", fontSize: "14px" }} data-testid="quality-result">
            <dt>{t("diag.verdict")}</dt>
            <dd class={result.verdict === "poor" ? "danger-text" : result.verdict === "fair" ? "warning-text" : "success-text"}>{t(`diag.${result.verdict}`)}</dd>
            <dt>{t("diag.mos")}</dt>
            <dd>{result.mos.toFixed(2)}</dd>
            <dt>{t("stats.rtt")}</dt>
            <dd>{result.rtt_ms} ms</dd>
            <dt>{t("stats.loss")}</dt>
            <dd>{result.loss_pct.toFixed(1)} %</dd>
            <dt>{t("stats.jitter")}</dt>
            <dd>{result.jitter_ms} ms</dd>
            <dt>{t("stats.codec")}</dt>
            <dd>{result.codec ?? "—"}</dd>
          </dl>
        )}
      </div>

      <h2>{t("diag.audio")}</h2>
      <div class="card" style={{ display: "grid", gap: "12px" }}>
        <p class="muted" style={{ margin: 0 }}>{t("diag.audio_help")}</p>
        <div class="row">
          <button type="button" class="btn soft" onClick={() => void toggleMic()}>
            <Icon name="mic" size={18} /> {mic ? t("diag.stop") : t("diag.test_mic")}
          </button>
          <div class="meter grow" style={{ flex: 1 }} role="meter" aria-label={t("settings.microphone")} aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
            <div style={{ width: `${pct}%` }} />
          </div>
        </div>
        {lvl?.error && <p class="danger-text">{t("diag.mic_error")}</p>}
        {silent && !lvl?.error && <p class="warning-text">{t("diag.no_mic_sound")}</p>}
        <div class="row">
          <button type="button" class="btn soft" onClick={() => void S.cmd({ cmd: "diag.tone", target: "player", seconds: 2 })}>
            <Icon name="play" size={18} /> {t("diag.test_speaker")}
          </button>
          <button type="button" class="btn soft" onClick={() => void S.cmd({ cmd: "diag.tone", target: "alert", seconds: 2 })}>
            <Icon name="phone" size={18} /> {t("diag.test_ring")}
          </button>
        </div>
      </div>

      <h2>{t("diag.browser")}</h2>
      <div class="list">
        {caps.map(([k, ok]) => (
          <div class="item">
            <div class="grow">{t(k)}</div>
            <span class={ok ? "success-text" : "warning-text"}>{ok ? t("diag.yes") : t("diag.no")}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
